import React from 'react';

// Helper function to get age from date of birth
const getAge = dob => {
  if (!dob) return '';
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Function to convert a name to title case
const toTitleCase = (name) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Function to generate gradient colors from primary color
const getGradientColors = (color) => {
  // More explicit color handling for the gradient
  let darker;
  let lighter;
  
  try {
    // For hex colors
    if (color.startsWith('#')) {
      // Convert hex to RGB for better manipulation
      const r = parseInt(color.substr(1, 2), 16);
      const g = parseInt(color.substr(3, 2), 16);
      const b = parseInt(color.substr(5, 2), 16);
      
      // Create darker shade
      const darkerR = Math.max(0, r - 60);
      const darkerG = Math.max(0, g - 60);
      const darkerB = Math.max(0, b - 60);
      
      // Create lighter shade
      const lighterR = Math.min(255, r + 30);
      const lighterG = Math.min(255, g + 30);
      const lighterB = Math.min(255, b + 30);
      
      darker = `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
      lighter = `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;
    } else {
      // Fallback for non-hex colors
      darker = color;
      lighter = color;
    }
  } catch (e) {
    // If any error in parsing, use original color
    darker = color;
    lighter = color;
  }
  
  return {
    start: lighter,
    middle: color,
    end: darker
  };
};

// Info item for student details
const InfoItem = ({ icon, label, value }) => {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="text-white">{icon}</span>
      <span className="font-semibold text-white/90">{label}:</span> 
      <span className="text-white">{value || '-'}</span>
    </div>
  );
};

// Decorative school-themed icons for the background
const BackgroundDecorations = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Book icon */}
      <svg 
        className="absolute text-white/10 w-24 h-24 top-10 left-10 md:left-20" 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="currentColor"
      >
        <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
      
      {/* Pencil icon */}
      <svg 
        className="absolute text-white/10 w-20 h-20 top-20 right-10 md:right-20" 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="currentColor"
      >
        <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z" />
        <path d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z" />
      </svg>
      
      {/* Globe icon */}
      <svg 
        className="absolute text-white/10 w-28 h-28 bottom-10 right-20 lg:right-32" 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="currentColor"
      >
        <path d="M21.721 12.752a9.711 9.711 0 00-.945-5.003 12.754 12.754 0 01-4.339 2.708 18.991 18.991 0 01-.214 4.772 17.165 17.165 0 005.498-2.477zM14.634 15.55a17.324 17.324 0 00.332-4.647c-.952.227-1.945.347-2.966.347-1.021 0-2.014-.12-2.966-.347a17.515 17.515 0 00.332 4.647 17.385 17.385 0 005.268 0zM9.772 17.119a18.963 18.963 0 004.456 0A17.182 17.182 0 0112 21.724a17.18 17.18 0 01-2.228-4.605zM7.777 15.23a18.87 18.87 0 01-.214-4.774 12.753 12.753 0 01-4.34-2.708a9.711 9.711 0 00-.944 5.004a17.165 17.165 0 005.498 2.477zM21.356 14.752a9.765 9.765 0 01-7.478 6.817a18.64 18.64 0 001.988-4.718 18.627 18.627 0 005.49-2.098zM2.644 14.752c1.682.971 3.53 1.688 5.49 2.099a18.64 18.64 0 001.988 4.718 9.765 9.765 0 01-7.478-6.816zM13.878 2.43a9.755 9.755 0 016.116 3.986 11.267 11.267 0 01-3.746 2.504a18.63 18.63 0 00-2.37-6.49zM12 2.276a17.152 17.152 0 012.805 7.121c-.897.23-1.837.353-2.805.353-.968 0-1.908-.122-2.805-.353A17.151 17.151 0 0112 2.276zM10.122 2.43a18.629 18.629 0 00-2.37 6.49 11.266 11.266 0 01-3.746-2.504a9.754 9.754 0 016.116-3.985z" />
      </svg>
      
      {/* Calculator icon */}
      <svg 
        className="absolute text-white/10 w-16 h-16 bottom-20 left-20 lg:left-32" 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="currentColor"
      >
        <path fillRule="evenodd" d="M6.32 1.827a49.255 49.255 0 0111.36 0c1.497.174 2.57 1.46 2.57 2.93V19.5a3 3 0 01-3 3H6.75a3 3 0 01-3-3V4.757c0-1.47 1.073-2.756 2.57-2.93zM7.5 11.25a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H8.25a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H8.25zm-.75 3a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H8.25a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V18a.75.75 0 00-.75-.75H8.25zm1.748-6a.75.75 0 01.75-.75h.007a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.007a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.335.75.75.75h.007a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75h-.007zm-.75 3a.75.75 0 01.75-.75h.007a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.007a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.335.75.75.75h.007a.75.75 0 00.75-.75V18a.75.75 0 00-.75-.75h-.007zm1.754-6a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.008a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75h-.008zm-.75 3a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.008a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V18a.75.75 0 00-.75-.75h-.008zm1.748-6a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.008a.75.75 0 01-.75-.75v-.008zm.75 1.5a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75h-.008zm-8.25-6A.75.75 0 018.25 6h7.5a.75.75 0 01.75.75v.75a.75.75 0 01-.75.75h-7.5a.75.75 0 01-.75-.75v-.75zm9 9a.75.75 0 00-1.5 0V18a.75.75 0 001.5 0v-2.25z" clipRule="evenodd" />
      </svg>
    </div>
  );
};

// Achievement badge component
const AchievementBadge = ({ text }) => {
  return (
    <div className="absolute top-6 right-6 md:right-10 bg-yellow-400 text-blue-900 font-bold py-1 px-3 rounded-full transform rotate-12 shadow-lg text-sm">
      {text}
    </div>
  );
};

const StudentProfileHeader = ({ report }) => {
  const primaryColor = report.primaryColor || '#0891b2';
  const gradientColors = getGradientColors(primaryColor);
  
  // Determine if we should show an achievement badge
  const showAchievement = report.achievement || (report.grades && report.grades.some(g => g.score > 90));
  const achievementText = report.achievement || "Star Student!";
  
  return (
    <div 
      className="relative w-full overflow-hidden rounded-b-3xl shadow-2xl print:shadow-none page-item keep-together"
      style={{ 
        background: `radial-gradient(circle at top left, ${gradientColors.start}, ${gradientColors.middle} 40%, ${gradientColors.end} 90%)`,
        minHeight: '250px',
      }}
    >
      {/* Background decorative elements */}
      <BackgroundDecorations />
      
      {/* { <AchievementBadge text={achievementText} />} */}
      
      {/* Main content container */}
      <div className="relative z-10 w-full flex flex-col md:flex-row items-center md:items-start p-6 pt-12 md:p-10">
        {/* Profile image container */}
        <div className="flex-shrink-0 relative">
          <div className="w-36 h-36 md:w-44 md:h-44 lg:w-48 lg:h-48 rounded-full relative">
            {/* Profile frame with glowing effect */}
            <div className="absolute -inset-1 rounded-full bg-white/50 backdrop-blur-sm shadow-lg"></div>
            {/* Photo frame inner ring */}
            <div className="absolute inset-0 rounded-full border-4 border-white/80"></div>
            {/* Student photo */}
            <img
              src={report.profileImage || '/assets/logo.png'}
              alt={report.studentName}
              className="w-full h-full rounded-full object-cover relative border-2 border-white/90"
            />
          </div>
        </div>
        
        {/* Student details container */}
        <div className="mt-6 md:mt-0 md:ml-8 lg:ml-12 text-center md:text-left flex-grow">
          {/* Student name */}
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl text-white mb-1 tracking-wide"
            style={{ fontFamily: "'LucyTheCat', sans-serif", }}
          >
            {toTitleCase(report.studentName)}
          </h2>
          
          {/* Report title */}
          <h3 className="text-white/90 text-lg md:text-xl mb-3 font-medium" >
            Holistic Progress Report  {report.academicYear}
          </h3>
          
          {/* Student ID or other identifier if available */}
          {report.studentId && (
            <p className="text-white/80 mb-4">
              ID: {report.studentId}
            </p>
          )}
          
          {/* Unified student information container */}
          <div className="bg-white/20 backdrop-blur-sm px-6 py-4 rounded-xl shadow-lg mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <InfoItem 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                } 
                label="Class" 
                value={report.Class + "-" + report.sec}
              />
              
              <InfoItem 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                } 
                label="Term" 
                value="Term1" 
              />
              
              <InfoItem 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                } 
                label="Gender" 
                value={report.gender || 'Male'} 
              />
              
              <InfoItem 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6 3a1 1 0 011-1h.01a1 1 0 010 2H7a1 1 0 01-1-1zm2 3a1 1 0 00-2 0v1a2 2 0 00-2 2v1a2 2 0 00-2 2v.683a3.7 3.7 0 011.055.485 1.704 1.704 0 001.89 0 3.704 3.704 0 014.11 0 1.704 1.704 0 001.89 0 3.704 3.704 0 014.11 0 1.704 1.704 0 001.89 0A3.7 3.7 0 0118 12.683V12a2 2 0 00-2-2V9a2 2 0 00-2-2V6a1 1 0 10-2 0v1h-1V6a1 1 0 10-2 0v1H8V6zm10 8.868a3.704 3.704 0 01-4.055-.036 1.704 1.704 0 00-1.89 0 3.704 3.704 0 01-4.11 0 1.704 1.704 0 00-1.89 0A3.704 3.704 0 012 14.868V17a1 1 0 001 1h14a1 1 0 001-1v-2.132zM9 3a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm3 0a1 1 0 011-1h.01a1 1 0 110 2H13a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                } 
                label="Age" 
                value={getAge(report.dob) ? `${getAge(report.dob)} yrs` : undefined} 
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Wave/curve at the bottom of the header */}
      <div className="absolute bottom-0 left-0 w-full h-12">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="absolute bottom-0 left-0 w-full h-full">
          <path 
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V0C50.63,0,102.4,15.07,151.07,26.19,191.71,35.56,236.95,47.57,321.39,56.44Z" 
            className="fill-white"
          ></path>
        </svg>
      </div>
    </div>
  );
};

export default StudentProfileHeader; 